import { Editor, Transforms, Node, Path } from 'slate';
import types from '../../elements/types';
import HelptapEditor from '../../helpers';

function getColumnsCount(table) {

  if (typeof table.properties.columnsCount === 'undefined') {

    if (Array.isArray(table.children[0].children) === false) {
      return 1;
    }

    return table.children[0].children.length;
  }

  return table.properties.columnsCount;

}

export default function withTable(editor) {

  const {
    deleteBackward,
    insertText,
    insertBreak,
    normalizeNode
  } = editor;

  editor.normalizeNode = ([node, path]) => {

    if (node.type === types.TABLE_CELL) {
      if (node.children.length === 0) {
        Transforms.removeNodes(editor, { at: path });
        return; // Return to re-trigger normalization
      }
    } else if (node.type === types.TABLE_ROW) {
      const children = node.children;
      for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i];
        if (child.type !== types.TABLE_CELL) {
          Transforms.removeNodes(editor, { at: [...path, i] });
          return; // Return after removing non-cell to allow re-normalization
        }
      }
      if (children.length === 0) {
        Transforms.removeNodes(editor, { at: path });
        return; // Return after removing the row
      }
    } else if (node.type === types.TABLE) {

      const children = node.children;
      if (children.length === 0) {
        Transforms.removeNodes(editor, { at: path });
        return; // Return after removing empty table
      }
      const columnsCount = getColumnsCount(node);
      for (let i = children.length - 1; i >= 0; i--) {
        const row = children[i];
        if (row.type !== types.TABLE_ROW) {
          Transforms.removeNodes(editor, { at: [...path, i] });
          return; // Return after removing non-row
        }
        const cells = row.children;
        if (cells.length > columnsCount) {
          for (let j = columnsCount; j < cells.length; j++) {
            Transforms.removeNodes(editor, { at: [...path, i, j] });
            return; // Return after removing excess cells
          }
        } else if (cells.length < columnsCount) {
          const missingCells = Array.from({ length: columnsCount - cells.length }, () => HelptapEditor.table.buildCellNode());
          Transforms.insertNodes(editor, missingCells, { at: [...path, i, cells.length] });
          return; // Return after inserting missing cells
        }
      }
    }

    // If no normalization was required, proceed with default normalization
    normalizeNode([node, path]);
  };

  editor.insertText = (text) => {
    if (text === '\t') {
      const [table] = Editor.nodes(editor, {
        match: (n) => n.type === types.TABLE,
        mode: 'lowest',
      });

      if (table) {
        const [currentCell] = Editor.nodes(editor, {
          match: (n) => n.type === types.TABLE_CELL,
          mode: 'lowest',
        });

        const nextCell = Editor.next(editor, {
          match: (n) => n.type === types.TABLE_CELL,
          mode: 'lowest',
        });

        if (nextCell) {
          Transforms.select(editor, nextCell[1]);
        } else {
          const [currentRow] = Editor.nodes(editor, {
            match: (n) => n.type === types.TABLE_ROW,
            mode: 'lowest',
          });

          const nextRow = Editor.next(editor, {
            at: currentRow[1],
            match: (n) => n.type === types.TABLE_ROW,
            mode: 'lowest',
          });

          if (nextRow) {
            const [firstCell] = Editor.nodes(editor, {
              at: nextRow[1],
              match: (n) => n.type === types.TABLE_CELL,
              mode: 'lowest',
            });

            Transforms.select(editor, firstCell[1]);
          }
        }
        return;
      }
    }

    insertText(text);
  };

  editor.insertBreak = () => {
    const [table] = Editor.nodes(editor, {
      match: (n) => n.type === types.TABLE,
      mode: 'lowest',
    });

    if (table) {
      const tableNode = table[0];
      const columnsCount = getColumnsCount(tableNode);
      const rowsCount = tableNode.children.length;
      const [currentCell] = Editor.nodes(editor, {
        match: (n) => n.type === types.TABLE_CELL,
        mode: 'lowest',
      });

      const [currentRow] = Editor.nodes(editor, {
        match: (n) => n.type === types.TABLE_ROW,
        mode: 'lowest',
      });

      const currentRowNode = currentRow[0];
      const currentRowPath = currentRow[1];
      const isRowEmpty = Node.string(currentRowNode).trim() === '';
      const isCellEmpty = Node.string(currentCell[0]).trim() === '';

      if (isCellEmpty === true) {

        if (isRowEmpty === true) {

          if (currentRowPath[currentRowPath.length - 1] === rowsCount - 1) {
            HelptapEditor.table.replaceRowForUser(editor, currentRow[1]);
          } else {
            HelptapEditor.table.insertRowForUser(editor, Path.next(currentRow[1]), columnsCount);
          }

        } else {
          HelptapEditor.table.insertRowForUser(editor, Path.next(currentRow[1]), columnsCount);
        }

      } else {
        HelptapEditor.table.insertRowForUser(editor, Path.next(currentRow[1]), columnsCount);
      }

      return;
    }

    insertBreak();
  };

  editor.deleteBackward = (unit) => {

    const [table] = Editor.nodes(editor, {
      match: (n) => n.type === types.TABLE,
      mode: 'lowest',
    });

    if (table) {

      const [currentCell] = Editor.nodes(editor, {
        match: (n) => n.type === types.TABLE_CELL,
        mode: 'lowest',
      });
      const [currentRow] = Editor.nodes(editor, {
        match: (n) => n.type === types.TABLE_ROW,
        mode: 'lowest',
      });
      const currentCellNode = currentCell[0];
      const currentCellPath = currentCell[1];
      const currentRowNode = currentRow[0];
      const currentRowPath = currentRow[1];

      const isCellEmpty = Node.string(currentCellNode).trim() === '';
      const isRowEmpty = Node.string(currentRowNode).trim() === '';

      if (isCellEmpty === true) {

        Editor.withoutNormalizing(editor, () => {

          if (Path.hasPrevious(currentCellPath) === false) {

            if (isRowEmpty === true) {
              HelptapEditor.table.removeRow(editor, currentRowPath);
            }

            if (Path.hasPrevious(currentRowPath) === true) {
              const previousRowPath = Path.previous(currentRowPath);
              HelptapEditor.table.gotoCellEnd(editor, [...previousRowPath, currentRowNode.children.length - 1]);
            }

          } else {

            const previousCellPath = Path.previous(currentCellPath);
            HelptapEditor.table.gotoCellEnd(editor, previousCellPath);

          }

        });

        return;

      }

    }

    deleteBackward(unit);
  };

  return editor;
}